import { type ReadonlyVec2, vec2 } from 'gl-matrix';

export { findClosestAnchorWithinRadius } from './anchor.ts';

import { getPointOnCubicBezier, getTangentOnCubicBezier } from '@canvas/bezier';

import { walkPathSegments } from './path-traversal.ts';
import { getPointOnArc, getTangentOnArc } from './segments/arc.ts';
import { createCubicBezierFromSegment } from './segments/cubic-bezier.ts';
import { getPointOnLine } from './segments/line.ts';
import { getSegmentAtPathProportion } from './segments/segments.ts';
import {
  type PathSegment,
  type PathSegmentLine,
  type Line,
  type PathSegmentArc,
  type PathProportion,
} from './types.ts';

export type { PathSegment, PathSegmentLine, PathSegmentArc, Line };

/**
 * Gets the start point of a segment in a path segment array
 *
 * @param start
 * @param segments
 * @param segment
 * @returns
 */
export function getSegmentStart(
  start: ReadonlyVec2,
  segments: PathSegment[],
  segmentToFind: PathSegment,
) {
  let result: ReadonlyVec2 | undefined;
  walkPathSegments(start, segments, (start, _end, segment) => {
    if (segment === segmentToFind) {
      result = start;
      return false;
    }
  });
  return result;
}

/**
 * Gets the point and a tangent on a path, given the path and a proportion from 0-1
 * where 0 is the start of the path and 1 is the end
 * This returns a tuple containing the point (vec2)
 * and the angle of the tangent of the path at that point
 * or undefined if the proportion is out of range
 *
 * @param start
 * @param segments
 * @param proportion
 * @returns
 */

export function getPointAndTangentOnPath(
  start: ReadonlyVec2,
  segments: PathSegment[],
  proportion: PathProportion,
): [vec2, number] {
  // TODO: The `!segments` guard is due to a customer board crashing due to an undefined segments array.
  // However, such a value shouldn't be possible given the types allowed by this function - long-term this should be solved at the caller.
  if (!segments || segments.length === 0) {
    return [vec2.clone(start), 0];
  }

  const segmentInRange = getSegmentAtPathProportion(segments, proportion);
  // this cast to vec2 is safe because segmentInRange.segment is pulled from
  // the segments array above and so will be guaranteed to be found
  const segmentStart = getSegmentStart(start, segments, segmentInRange.segment) as vec2;

  const { segment, proportionWithinSegment } = segmentInRange;
  if (segment.type === 'line') {
    const point = getPointOnLine(segmentStart, segment, proportionWithinSegment);
    const { angle } = segment;
    return [point, angle];
  } else if (segment.type === 'arc') {
    const point = getPointOnArc(segmentStart, segment, proportionWithinSegment);
    const angle = getTangentOnArc(segment, proportionWithinSegment);
    return [point, angle];
  } else if (segment.type === 'cubic-bezier') {
    const bezier = createCubicBezierFromSegment(segment, segmentStart);
    const point = getPointOnCubicBezier(vec2.create(), bezier, proportionWithinSegment);
    const angle = getTangentOnCubicBezier(bezier, proportionWithinSegment);
    return [point, angle];
  }
  throw new Error('unknown segment.type');
}
